import {
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  FormField,
  IconButton,
  IconButtonColorEnum,
  IconButtonShapeEnum,
  IconButtonSizeEnum,
  IconSizeEnum,
  SaveIcon,
  ScreenChargingVideoIcon,
  TextArea,
  Tooltip,
  Typography,
} from '@acceso/design-system';
import dayjs from 'dayjs';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ClipTranscription } from '@/components/ClipTranscription/ClipTranscription';
import { TimeInput } from '@/components/TimeInput/TimeInput';
import { VideoPlayer } from '@/components/Video';
import { TProgramBroadcast } from '@/features/broadcast-schedule/types';
import { BroadcastScheduleProgramBodySegmentDetailCardBodySegmentList } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentDetailCard/components/BroadcastScheduleProgramBodySegmentDetailCardBodySegmentList';
import { BroadcastScheduleProgramBodySegmentDetailCardBodySegmentTranscription } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentDetailCard/components/BroadcastScheduleProgramBodySegmentDetailCardBodySegmentTranscription';
import { useBroadcastScheduleProgramBodySegmentDetailCardBodySegmentListState } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentDetailCard/hooks/useBroadcastScheduleProgramBodySegmentDetailCardBodySegmentListState';
import { useBroadcastScheduleProgramBodySegmentDetailCardBodySegmentNavigationState } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentDetailCard/hooks/useBroadcastScheduleProgramBodySegmentDetailCardBodySegmentNavigationState';
import { useBroadcastScheduleContext } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/context/BroadcastScheduleProvider';
import { patchClipCache } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitTimeKeeping/utils/patch-clip-cache';
import { ESegmentToolkit } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { useGoNextOnEnded } from '@/features/broadcast-schedule-program/hooks/useGoNextOnEnded';
import { useProgramBroadcastFilters } from '@/stores/program-broadcast-filters';

import { usePatchClip } from '../../api/patch-clips';
import { useClipNavigation } from '../../hooks/use-clip-navigation';
import { useClipTranscriptionsPermissions } from '../../hooks/useClipTranscriptionsPermissions';

export function ClipDetailCardBody() {
  const { t } = useTranslation();
  const {
    isSegmentPlayerMode,
    visibleBox,
    activeClip,
    drawerLogo,
    videoPlayerRef,
    pendingSeekTime,
    videoControlRef,
    clipItems,
    presenceData,
    pinnedId,
    setPendingSeekTime,
    onDrawnRect,
    setVisibleBox,
    setPlayerVideoCurrentTime,
    setPlayerVideoDuration,
    setPlayerVideoIsPlaying,
    setSelectedMention,
    textToHighlight,
    playerVideoCurrentTime,
    activeSegment,
    segmentItems,
    setSegmentItems,
    setActiveClip,
    cutForm,
  } = useBroadcastScheduleProgram();

  const {
    setAutoplayingClips,
    handleEndPresence,
    handleGoNextClipPresence,
    handleSeekPresence,
    handleSeekClipPresence,
  } = useBroadcastScheduleContext();

  const { search } = useProgramBroadcastFilters();

  const { currentAbsoluteTime, earliestTime, latestTime, handleChangeAbsoluteTimeInput } =
    useBroadcastScheduleProgramBodySegmentDetailCardBodySegmentListState({
      items: segmentItems,
      setSegmentItems,
    });

  const [noteValue, setNoteValue] = React.useState('');
  const [isNoteDirty, setIsNoteDirty] = React.useState(false);

  React.useEffect(() => {
    setNoteValue(activeClip?.time_keeping_note ?? '');
    setIsNoteDirty(false);
  }, [activeClip?.id]);

  const { mutate: patchClipMutate } = usePatchClip({
    config: {
      onSuccess: data => {
        setIsNoteDirty(false);
        // Mirror the saved note into every cache entry that holds this clip plus the
        // in-memory activeClip, so that navigating clip→clip→clip restores the persisted
        // value instead of the pre-PATCH snapshot from the listing cache.
        patchClipCache(data.id, clip => ({
          ...clip,
          time_keeping_note: data.time_keeping_note,
        }));
        setActiveClip(prev =>
          prev && prev.id === data.id
            ? { ...prev, time_keeping_note: data.time_keeping_note }
            : prev
        );
      },
    },
  });

  const isTimekeeping = pinnedId === ESegmentToolkit.TIMEKEEPING;

  const { handleNextItem: handleNextItemClip, hasNextItem: hasNextItemClip } = useClipNavigation({
    data: clipItems,
  });
  const { handleNextItem: handleNextItemSegment, hasNextItem: hasNextItemSegment } =
    useBroadcastScheduleProgramBodySegmentDetailCardBodySegmentNavigationState({
      data: segmentItems,
    });

  const handleNextItem = isSegmentPlayerMode ? handleNextItemSegment : handleNextItemClip;
  const hasNextItem = isSegmentPlayerMode ? hasNextItemSegment : hasNextItemClip;

  const cutEnd = cutForm.watch('end_datetime');
  const cutStart = cutForm.watch('start_datetime');

  const { canSee: canSeeTranscriptions } = useClipTranscriptionsPermissions();

  const markers = React.useMemo(() => {
    if (!activeClip || !activeSegment || !isSegmentPlayerMode) return [];

    const segmentStart = dayjs(activeSegment.start_datetime);
    const segmentDuration = activeSegment.duration_in_seconds || 1;

    const result: { pos: number; type: 'start' | 'end' }[] = [];

    const clipStart = dayjs(cutStart || activeClip.start_datetime);
    const clipEnd = dayjs(cutEnd || activeClip.end_datetime || activeClip.start_datetime);

    const startOffset = clipStart.diff(segmentStart, 'second', true);
    if (startOffset >= 0 && startOffset <= segmentDuration) {
      result.push({ pos: startOffset / segmentDuration, type: 'start' });
    }

    const endOffset = clipEnd.diff(segmentStart, 'second', true);
    if (endOffset >= 0 && endOffset <= segmentDuration) {
      result.push({ pos: endOffset / segmentDuration, type: 'end' });
    }

    return result;
  }, [
    activeClip,
    activeClip?.start_datetime,
    activeClip?.end_datetime,
    activeSegment,
    activeSegment?.start_datetime,
    activeSegment?.duration_in_seconds,
    cutEnd,
    cutStart,
    isSegmentPlayerMode,
  ]);

  const handleGoNext = useGoNextOnEnded({
    hasNextItem,
    handleNextItem,
    videoControlRef,
    skip: isTimekeeping,
  });

  const handlePause = () => {
    setAutoplayingClips(false);
  };

  const updateNote = () => {
    if (!activeClip) return;
    patchClipMutate({ id: activeClip.id, time_keeping_note: noteValue ?? '' });
  };

  const getNotes = () => {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex flex-row items-center justify-between gap-2">
          <Typography
            fontSize={FontSizeEnum.Lg}
            weight={FontWeightEnum.Semibold}
            color={FontColorEnum.Dark10}
          >
            {t('Notas')}
          </Typography>
          <Tooltip help={t('Guardar nota')} grow>
            <IconButton
              shape={IconButtonShapeEnum.SQUARED}
              size={IconButtonSizeEnum.SM}
              color={IconButtonColorEnum.SECONDARY}
              onClick={updateNote}
            >
              <SaveIcon size={IconSizeEnum.SM} />
            </IconButton>
          </Tooltip>
        </div>
        <FormField>
          <TextArea
            value={noteValue}
            placeholder={t('Escriba una nota...')}
            onChange={e => {
              setNoteValue((e.target as HTMLTextAreaElement).value);
              setIsNoteDirty(true);
            }}
            className={isNoteDirty ? 'ring-2 ring-red-500' : ''}
          />
        </FormField>
        <div data-id="note-saved-warning" className={isNoteDirty ? '' : 'hidden'}>
          <Typography
            color={FontColorEnum.Primary}
            weight={FontWeightEnum.Regular}
            fontSize={FontSizeEnum.Xs}
          >
            {t('Nota modificada, no olvide Guardar presionando el botón azul.')}
          </Typography>
        </div>
      </div>
    );
  };

  const getAbsoluteTime = () => {
    return (
      <div className="mb-3 flex flex-row items-center gap-2">
        <Typography fontSize={FontSizeEnum.Md} color={FontColorEnum.Dark10}>
          {t('Tiempo absoluto del programa')}
        </Typography>
        <TimeInput
          initialValue={currentAbsoluteTime}
          minValue={earliestTime}
          maxValue={latestTime}
          onConfirm={formattedTime => {
            handleChangeAbsoluteTimeInput({
              target: { value: formattedTime },
            } as React.ChangeEvent<HTMLInputElement>);
          }}
        />
      </div>
    );
  };

  const getSummary = () => {
    return (
      <div className="flex flex-col gap-2">
        <Typography
          fontSize={FontSizeEnum.Lg}
          weight={FontWeightEnum.Semibold}
          color={FontColorEnum.Dark10}
        >
          {t('Resumen')}
        </Typography>
        <Typography
          fontSize={FontSizeEnum.Md}
          weight={FontWeightEnum.Regular}
          color={FontColorEnum.Dark10}
        >
          {activeClip?.body}
        </Typography>

        {canSeeTranscriptions && activeClip && activeClip.clip_transcriptions[0] && (
          <ClipTranscription
            transcriptions={{
              id: activeClip.clip_transcriptions[0].id,
              mentions: activeClip.clip_mentions,
              words: activeClip.clip_transcriptions[0].asr_recognition_results,
            }}
            seconds={playerVideoCurrentTime}
            textToHighlight={textToHighlight || ''}
            setSelectedMention={setSelectedMention}
            searchTextToHighlight={search}
          />
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex justify-center">
        {activeClip?.state === 'generating' ? (
          <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-bg-primary-25 px-2 py-4">
            <ScreenChargingVideoIcon size={IconSizeEnum.CUSTOM} className="h-40 w-40" />
            <Typography fontSize={FontSizeEnum.Xl} weight={FontWeightEnum.Bold}>
              {t('Generando video')}
            </Typography>
          </div>
        ) : (
          <VideoPlayer
            activeBox={visibleBox}
            checkProgress={handleGoNextClipPresence}
            controls={false}
            customControls
            drawerLogo={drawerLogo}
            presenceData={presenceData}
            markers={isSegmentPlayerMode ? markers : undefined}
            onDrawnRect={onDrawnRect}
            onEnded={handleGoNext}
            onEndPresence={handleEndPresence}
            onPause={handlePause}
            pendingSeekTime={pendingSeekTime}
            ref={videoPlayerRef}
            setActiveBox={setVisibleBox}
            setPendingSeekTime={setPendingSeekTime}
            setPlayerVideoCurrentTime={setPlayerVideoCurrentTime}
            setPlayerVideoDuration={setPlayerVideoDuration}
            setPlayerVideoIsPlaying={setPlayerVideoIsPlaying}
            url={
              (isSegmentPlayerMode ? activeSegment?.presigned_url : activeClip?.presigned_url) || ''
            }
            checkSeek={isSegmentPlayerMode ? handleSeekPresence : handleSeekClipPresence}
            onRefReady={ref => {
              if (videoControlRef && 'current' in videoControlRef) {
                (videoControlRef as React.MutableRefObject<unknown>).current = ref;
              }
            }}
          />
        )}
      </div>

      {isSegmentPlayerMode &&
        activeClip?.program_broadcast_id &&
        activeClip?.state !== 'generating' && (
          <BroadcastScheduleProgramBodySegmentDetailCardBodySegmentList
            programBroadcast={
              {
                id: activeClip.program_broadcast_id,
                name: activeClip.program_broadcast_id,
              } as TProgramBroadcast
            }
            items={segmentItems}
            setSegmentItems={setSegmentItems}
            videoHasError={false}
            disableSegmentEditing={true}
          />
        )}

      {isSegmentPlayerMode && activeClip?.state !== 'generating' ? (
        <BroadcastScheduleProgramBodySegmentDetailCardBodySegmentTranscription />
      ) : isTimekeeping ? (
        <>
          {getAbsoluteTime()}
          {getNotes()}
        </>
      ) : (
        getSummary()
      )}
    </div>
  );
}
